import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getUserIntelligence } from '../../services/api';

const TIER_COLORS = {
  free: { bg: '#374151', color: '#9ca3af' },
  essential: { bg: '#065f46', color: '#d1fae5' },
  plus: { bg: '#1e40af', color: '#dbeafe' },
  premium: { bg: '#5b21b6', color: '#ede9fe' },
};

const STATUS_COLORS = {
  new: { bg: '#1e3a5f', color: '#93c5fd' },
  active: { bg: '#065f46', color: '#6ee7b7' },
  at_risk: { bg: '#78350f', color: '#fbbf24' },
  stuck: { bg: '#7c2d12', color: '#fb923c' },
  churned: { bg: '#7f1d1d', color: '#fca5a5' },
};

const STATUS_LABELS = {
  new: 'New',
  active: 'Active',
  at_risk: 'At Risk',
  stuck: 'Stuck',
  churned: 'Churned',
};

const TIER_FILTERS = ['all', 'free', 'essential', 'plus', 'premium'];
const STATUS_FILTERS = ['all', 'new', 'active', 'at_risk', 'stuck', 'churned'];

function getEngagementColor(score) {
  if (score >= 70) return '#10b981';
  if (score >= 40) return '#f59e0b';
  return '#ef4444';
}

function getUsageColor(pct) {
  if (pct >= 80) return '#ef4444';
  if (pct >= 50) return '#f59e0b';
  return '#10b981';
}

function formatDate(dateStr) {
  if (!dateStr) return 'Never';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTimeAgo(dateStr) {
  if (!dateStr) return 'Never';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return '1d ago';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
}

const SORTABLE_COLUMNS = [
  { key: 'username', label: 'Username' },
  { key: 'email', label: 'Email' },
  { key: 'tier', label: 'Tier' },
  { key: 'days_on_platform', label: 'Days on Platform' },
  { key: 'last_active', label: 'Last Active' },
  { key: 'total_activities', label: 'Activities' },
  { key: 'screener_usage', label: 'Screener Usage' },
  { key: 'watchlist_usage', label: 'Watchlist Usage' },
  { key: 'engagement_score', label: 'Score' },
  { key: 'status', label: 'Status' },
];

function ReportsUserIntelligence({ onNavigateToJourney }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tierFilter, setTierFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'engagement_score', dir: 'desc' });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getUserIntelligence();
      setData(result);
    } catch (err) {
      setError(err.message || 'Failed to fetch user intelligence data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredUsers = useMemo(() => {
    if (!data?.users) return [];
    let users = data.users;

    if (tierFilter !== 'all') {
      users = users.filter((u) => u.tier === tierFilter);
    }
    if (statusFilter !== 'all') {
      users = users.filter((u) => u.status === statusFilter);
    }

    // Sort
    const { key, dir } = sortConfig;
    const sorted = [...users].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];

      // Handle nested usage objects
      if (key === 'screener_usage') {
        aVal = a.screener_usage?.percentage || 0;
        bVal = b.screener_usage?.percentage || 0;
      } else if (key === 'watchlist_usage') {
        aVal = a.watchlist_usage?.percentage || 0;
        bVal = b.watchlist_usage?.percentage || 0;
      }

      // Handle dates
      if (key === 'last_active' || key === 'date_joined') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }

      // Handle strings
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }

      if (aVal < bVal) return dir === 'asc' ? -1 : 1;
      if (aVal > bVal) return dir === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [data, tierFilter, statusFilter, sortConfig]);

  // Recalculate summary based on current filters
  const filteredSummary = useMemo(() => {
    if (!data?.summary) return null;
    // Always use full data summary for the cards (unfiltered counts)
    return data.summary;
  }, [data]);

  const handleSort = (columnKey) => {
    setSortConfig((prev) => ({
      key: columnKey,
      dir: prev.key === columnKey && prev.dir === 'desc' ? 'asc' : 'desc',
    }));
  };

  if (loading) {
    return (
      <div className="roles-loading">
        <div className="spinner"></div>
        <p>Loading user intelligence data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reports-error">
        <p>{error}</p>
        <button className="refresh-btn" onClick={fetchData}>Retry</button>
      </div>
    );
  }

  const summary = filteredSummary;
  const hotLeads = data?.hot_leads || [];

  return (
    <div className="user-intelligence">
      {/* Summary Cards */}
      <div className="user-intelligence-summary">
        {['free', 'essential', 'plus', 'premium'].map((tier) => {
          const tc = TIER_COLORS[tier];
          return (
            <div
              key={tier}
              className="summary-card"
              style={{ borderLeft: `4px solid ${tc.color}`, cursor: 'pointer' }}
              onClick={() => setTierFilter(tierFilter === tier ? 'all' : tier)}
            >
              <div style={{ fontSize: '28px', fontWeight: '700', color: tc.color }}>
                {summary?.tier_counts?.[tier] || 0}
              </div>
              <div style={{ fontSize: '12px', color: '#aeb9cc', textTransform: 'capitalize' }}>
                {tier}
              </div>
            </div>
          );
        })}
        <div className="summary-card" style={{ borderLeft: '4px solid #f59e0b', cursor: 'pointer' }}
          onClick={() => { setTierFilter('free'); setStatusFilter('active'); }}>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#f59e0b' }}>
            {summary?.hot_leads_count || 0}
          </div>
          <div style={{ fontSize: '12px', color: '#aeb9cc' }}>Hot Leads</div>
        </div>
        <div className="summary-card" style={{ borderLeft: '4px solid #fb923c', cursor: 'pointer' }}
          onClick={() => setStatusFilter(statusFilter === 'stuck' ? 'all' : 'stuck')}>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#fb923c' }}>
            {summary?.status_counts?.stuck || 0}
          </div>
          <div style={{ fontSize: '12px', color: '#aeb9cc' }}>Stuck</div>
        </div>
        <div className="summary-card" style={{ borderLeft: '4px solid #fbbf24', cursor: 'pointer' }}
          onClick={() => setStatusFilter(statusFilter === 'at_risk' ? 'all' : 'at_risk')}>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#fbbf24' }}>
            {summary?.status_counts?.at_risk || 0}
          </div>
          <div style={{ fontSize: '12px', color: '#aeb9cc' }}>At Risk</div>
        </div>
      </div>

      {/* Filters */}
      <div className="user-intelligence-filters">
        <div className="filter-group">
          <span className="filter-label">Tier:</span>
          {TIER_FILTERS.map((f) => (
            <button
              key={f}
              className={`section-tab ${tierFilter === f ? 'active' : ''}`}
              onClick={() => setTierFilter(f)}
              style={{ padding: '5px 12px', fontSize: '12px' }}
            >
              {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        <div className="filter-group">
          <span className="filter-label">Status:</span>
          {STATUS_FILTERS.map((f) => (
            <button
              key={f}
              className={`section-tab ${statusFilter === f ? 'active' : ''}`}
              onClick={() => setStatusFilter(f)}
              style={{ padding: '5px 12px', fontSize: '12px' }}
            >
              {f === 'all' ? 'All' : STATUS_LABELS[f] || f}
            </button>
          ))}
        </div>
        <button className="refresh-btn" onClick={fetchData} disabled={loading} style={{ marginLeft: 'auto' }}>
          Refresh
        </button>
      </div>

      {/* User Table */}
      <div className="user-intelligence-table">
        <table>
          <thead>
            <tr>
              {SORTABLE_COLUMNS.map((col) => (
                <th key={col.key} onClick={() => handleSort(col.key)}>
                  {col.label}
                  {sortConfig.key === col.key && (
                    <span style={{ marginLeft: '4px', fontSize: '10px' }}>
                      {sortConfig.dir === 'asc' ? '\u25B2' : '\u25BC'}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={SORTABLE_COLUMNS.length} style={{ textAlign: 'center', padding: '30px', color: '#888' }}>
                  No users match the current filters.
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => {
                const tc = TIER_COLORS[user.tier] || TIER_COLORS.free;
                const sc = STATUS_COLORS[user.status] || STATUS_COLORS.active;
                return (
                  <tr key={user.id}>
                    <td>
                      <span
                        className="username-link"
                        onClick={() => onNavigateToJourney && onNavigateToJourney(user.username)}
                      >
                        {user.username}
                      </span>
                    </td>
                    <td style={{ color: '#888', fontSize: '12px' }}>{user.email || '-'}</td>
                    <td>
                      <span
                        className="tier-badge"
                        style={{ backgroundColor: tc.bg, color: tc.color }}
                      >
                        {user.tier_display}
                      </span>
                    </td>
                    <td>{user.days_on_platform}d</td>
                    <td style={{ color: '#aeb9cc', fontSize: '12px' }}>
                      {formatTimeAgo(user.last_active)}
                    </td>
                    <td>{user.total_activities}</td>
                    <td>
                      <UsageBar usage={user.screener_usage} />
                    </td>
                    <td>
                      <UsageBar usage={user.watchlist_usage} />
                    </td>
                    <td>
                      <span style={{
                        fontWeight: '700',
                        fontSize: '14px',
                        color: getEngagementColor(user.engagement_score),
                      }}>
                        {user.engagement_score}
                      </span>
                    </td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ backgroundColor: sc.bg, color: sc.color }}
                      >
                        {STATUS_LABELS[user.status] || user.status}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div style={{ color: '#888', fontSize: '12px', marginBottom: '20px', textAlign: 'right' }}>
        Showing {filteredUsers.length} of {data?.users?.length || 0} users
      </div>

      {/* Hot Leads Section */}
      {hotLeads.length > 0 && (
        <div className="hot-leads-section">
          <h3 style={{ color: '#f59e0b', margin: '0 0 6px 0', fontSize: '16px' }}>
            Hot Leads
          </h3>
          <p style={{ color: '#aeb9cc', fontSize: '13px', margin: '0 0 16px 0' }}>
            Free-tier users with high engagement (score 60+) — most likely to convert to paid.
          </p>
          <div className="hot-leads-grid">
            {hotLeads.map((lead) => (
              <div
                key={lead.id}
                className="hot-lead-card"
                onClick={() => onNavigateToJourney && onNavigateToJourney(lead.username)}
                style={{ cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <span style={{ color: '#fff', fontWeight: '600', fontSize: '14px' }}>
                    {lead.username}
                  </span>
                  <span style={{
                    fontWeight: '700',
                    fontSize: '18px',
                    color: getEngagementColor(lead.engagement_score),
                  }}>
                    {lead.engagement_score}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px' }}>
                  <div>
                    <span style={{ color: '#888' }}>Screener: </span>
                    <span style={{ color: getUsageColor(lead.screener_usage?.percentage || 0) }}>
                      {lead.screener_usage?.percentage || 0}%
                    </span>
                  </div>
                  <div>
                    <span style={{ color: '#888' }}>Watchlist: </span>
                    <span style={{ color: getUsageColor(lead.watchlist_usage?.percentage || 0) }}>
                      {lead.watchlist_usage?.percentage || 0}%
                    </span>
                  </div>
                  <div>
                    <span style={{ color: '#888' }}>Platform: </span>
                    <span style={{ color: '#ccc' }}>{lead.days_on_platform}d</span>
                  </div>
                  <div>
                    <span style={{ color: '#888' }}>Last seen: </span>
                    <span style={{ color: '#ccc' }}>{formatTimeAgo(lead.last_active)}</span>
                  </div>
                </div>
                <div style={{ marginTop: '10px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {lead.engagement_breakdown && Object.entries(lead.engagement_breakdown).map(([k, v]) => (
                    <span key={k} style={{
                      fontSize: '10px',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      background: '#2a2a2a',
                      color: v >= 20 ? '#10b981' : v >= 10 ? '#f59e0b' : '#888',
                    }}>
                      {k}: {v}/25
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function UsageBar({ usage }) {
  if (!usage || usage.limit <= 0) {
    return <span style={{ color: '#888', fontSize: '12px' }}>-</span>;
  }
  const pct = usage.percentage || 0;
  return (
    <div className="usage-bar-container">
      <div className="usage-bar">
        <div
          className="usage-bar-fill"
          style={{
            width: `${Math.min(100, pct)}%`,
            backgroundColor: getUsageColor(pct),
          }}
        />
      </div>
      <span style={{ fontSize: '11px', color: '#888' }}>
        {usage.used}/{usage.limit === -1 ? '\u221E' : usage.limit}
      </span>
    </div>
  );
}

export default ReportsUserIntelligence;
